import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Upload, 
  X, 
  Download, 
  Copy, 
  Loader2,
  CheckCircle,
  ArrowLeft,
  Video,
  Play,
  Clock,
  FileText,
  Wand2,
  ExternalLink,
  Image
} from "lucide-react";
import { toast } from "sonner";
import { AppHeader } from './AppHeader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VIDEO_PRESETS = [
  { key: "portrait_9_16", label: "Portrait 9:16", dimensions: "1080×1920", primary: true },
  { key: "portrait_4_5", label: "Portrait 4:5", dimensions: "1080×1350" },
  { key: "square_1_1", label: "Square 1:1", dimensions: "1080×1080" },
  { key: "landscape_16_9", label: "Landscape 16:9", dimensions: "1920×1080" },
];

const LOGO_POSITIONS = [
  { value: "north_west", label: "Top Left" },
  { value: "north", label: "Top Centre" },
  { value: "north_east", label: "Top Right" },
  { value: "west", label: "Middle Left" },
  { value: "center", label: "Centre" },
  { value: "east", label: "Middle Right" },
  { value: "south_west", label: "Bottom Left" },
  { value: "south", label: "Bottom Centre" },
  { value: "south_east", label: "Bottom Right" },
];

// Characters per second for duration calculation (TTS average ~175-200 wpm)
const CHARS_PER_SECOND = 29;

function PresentourBuilder({ onBack, scriptData }) {
  // Script sections from ScriptBuilder - enhanced with media type
  const [sections, setSections] = useState(() => {
    const baseSections = scriptData?.sections || [];
    return baseSections.map(s => ({
      ...s,
      mediaType: 'video', // 'video' or 'image'
      assignedVideo: null,
      assignedImage: null
    }));
  });
  
  // Upload state
  const [uploadedClips, setUploadedClips] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);
  
  // Section image upload state
  const [uploadingSectionImage, setUploadingSectionImage] = useState(null);
  const sectionImageRefs = useRef({});

  // Smart file management
  const [smartFileManagement, setSmartFileManagement] = useState(false);

  // Format options
  const [selectedFormats, setSelectedFormats] = useState(["portrait_9_16"]);
  
  // Transition option
  const [transition, setTransition] = useState("none");

  // Branding
  const [logoEnabled, setLogoEnabled] = useState(false);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [logoPosition, setLogoPosition] = useState("south_east");
  const [logoScale, setLogoScale] = useState(40);
  const [logoPadding, setLogoPadding] = useState(20);
  const [logoOpacity, setLogoOpacity] = useState(80);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);

  // Results
  const [generatedVideos, setGeneratedVideos] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingMessage, setProcessingMessage] = useState("");
  const [processingStep, setProcessingStep] = useState(0);

  // Load company logo on mount
  useEffect(() => {
    loadCompanyLogo();
  }, []);

  const loadCompanyLogo = async () => {
    try {
      const response = await axios.get(`${API}/company/logo`);
      if (response.data.success && response.data.logo) {
        setCompanyLogo(response.data.logo);
      }
    } catch (error) {
      console.error("Error loading company logo:", error);
    }
  };

  // Handle video upload
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    if (uploadedClips.length + files.length > 20) {
      toast.error("Maximum 20 clips allowed");
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      files.forEach(file => formData.append("files", file));

      const response = await axios.post(`${API}/video/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percent === 100 ? 99 : percent);
        }
      });

      setUploadProgress(100);
      
      if (response.data.success) {
        const newClips = response.data.uploaded.map((clip, idx) => ({
          ...clip,
          assignedSection: null, // Which script section this video is for
        }));
        setUploadedClips(prev => [...prev, ...newClips]);
        toast.success(`${response.data.total_uploaded} clip(s) uploaded`);
        
        // If smart file management is enabled, try to auto-match
        if (smartFileManagement) {
          autoMatchVideosToSections(newClips);
        }
      }

      if (response.data.errors?.length > 0) {
        response.data.errors.forEach(err => {
          toast.error(`Failed: ${err.filename}`);
        });
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload videos");
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // Auto-match videos to sections based on filename (placeholder for future)
  const autoMatchVideosToSections = (clips) => {
    // This is a placeholder for future smart file management
    // Will match filenames like "kitchen.mp4" to "Kitchen" section
    toast.info("Smart File Management: Auto-matching coming soon!");
  };

  // Toggle media type for a section
  const toggleSectionMediaType = (sectionIndex) => {
    setSections(prev => prev.map((section, idx) => {
      if (idx === sectionIndex) {
        const newType = section.mediaType === 'video' ? 'image' : 'video';
        return {
          ...section,
          mediaType: newType,
          // Clear the other type when switching
          assignedVideo: newType === 'video' ? section.assignedVideo : null,
          assignedImage: newType === 'image' ? section.assignedImage : null
        };
      }
      return section;
    }));
  };

  // Assign video to section
  const assignVideoToSection = (sectionIndex, clipId) => {
    setSections(prev => prev.map((section, idx) => {
      if (idx === sectionIndex) {
        const clip = uploadedClips.find(c => c.id === clipId);
        return { ...section, assignedVideo: clip || null };
      }
      return section;
    }));
  };

  // Handle section image upload
  const handleSectionImageUpload = async (e, sectionIndex) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingSectionImage(sectionIndex);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(`${API}/presentour/upload-image`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      if (response.data.success) {
        setSections(prev => prev.map((section, idx) => {
          if (idx === sectionIndex) {
            return {
              ...section,
              assignedImage: {
                public_id: response.data.public_id,
                url: response.data.url,
                secure_url: response.data.secure_url
              }
            };
          }
          return section;
        }));
        toast.success("Image uploaded");
      }
    } catch (error) {
      console.error("Section image upload error:", error);
      toast.error("Failed to upload image");
    } finally {
      setUploadingSectionImage(null);
      if (sectionImageRefs.current[sectionIndex]) {
        sectionImageRefs.current[sectionIndex].value = "";
      }
    }
  };

  // Remove section image
  const removeSectionImage = async (sectionIndex) => {
    const section = sections[sectionIndex];
    if (section?.assignedImage?.public_id) {
      try {
        await axios.delete(`${API}/presentour/image/${encodeURIComponent(section.assignedImage.public_id)}`);
      } catch (error) {
        console.error("Error deleting section image:", error);
      }
    }
    setSections(prev => prev.map((s, idx) => {
      if (idx === sectionIndex) {
        return { ...s, assignedImage: null };
      }
      return s;
    }));
  };

  // Handle logo upload
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploadingLogo(true);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(`${API}/company/logo`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      if (response.data.success) {
        setCompanyLogo({
          public_id: response.data.public_id,
          url: response.data.url,
          secure_url: response.data.secure_url
        });
        toast.success("Logo uploaded");
      }
    } catch (error) {
      console.error("Logo upload error:", error);
      toast.error("Failed to upload logo");
    } finally {
      setIsUploadingLogo(false);
      if (logoInputRef.current) {
        logoInputRef.current.value = "";
      }
    }
  };

  // Remove clip
  const removeClip = async (clipId, publicId) => {
    setUploadedClips(prev => prev.filter(clip => clip.id !== clipId));
    // Also clear from any sections
    setSections(prev => prev.map(section => {
      if (section.assignedVideo?.id === clipId) {
        return { ...section, assignedVideo: null };
      }
      return section;
    }));
    
    try {
      await axios.delete(`${API}/video/clip/${encodeURIComponent(publicId)}`);
    } catch (error) {
      console.error("Error deleting clip:", error);
    }
  };

  // Toggle format selection
  const toggleFormat = (formatKey) => {
    setSelectedFormats(prev => {
      if (prev.includes(formatKey)) {
        if (prev.length === 1) {
          toast.error("At least one format must be selected");
          return prev;
        }
        return prev.filter(f => f !== formatKey);
      }
      return [...prev, formatKey];
    });
  };

  // Format duration
  const formatDuration = (seconds) => {
    if (!seconds) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Check if a section has media assigned
  const sectionHasMedia = (section) => {
    if (section.mediaType === 'video') {
      return section.assignedVideo !== null;
    } else {
      return section.assignedImage !== null;
    }
  };

  // Check if all sections have media assigned
  const allSectionsHaveMedia = () => {
    return sections.every(section => sectionHasMedia(section));
  };

  // Calculate total video duration
  const getTotalDuration = () => {
    return sections.reduce((sum, section) => sum + (section.duration || 0), 0);
  };

  // Generate Presentour video
  const handleGenerate = async () => {
    if (sections.length === 0) {
      toast.error("No script sections available");
      return;
    }

    if (!allSectionsHaveMedia()) {
      toast.error("Please assign media (video or image) to each script section");
      return;
    }

    // Check if at least one section has a video (required for stitching)
    const hasAtLeastOneVideo = sections.some(s => s.mediaType === 'video' && s.assignedVideo);
    if (!hasAtLeastOneVideo) {
      toast.error("At least one section must have a video clip for proper stitching");
      return;
    }

    if (selectedFormats.length === 0) {
      toast.error("Please select at least one output format");
      return;
    }

    setIsProcessing(true);
    setProcessingStep(0);
    setProcessingMessage("Analysing script sections...");
    setGeneratedVideos(null);

    // Scroll to top
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);

    // Dynamic processing messages
    const processingSteps = [
      "Analysing script sections...",
      "Processing media clips...",
      "Trimming to script duration...",
      "Stitching segments...",
      "Adding transitions...",
      "Rendering final output...",
      "Almost there..."
    ];

    const messageInterval = setInterval(() => {
      setProcessingStep(prev => {
        const next = prev + 1;
        if (next < processingSteps.length) {
          setProcessingMessage(processingSteps[next]);
        }
        return next;
      });
    }, 4000);

    try {
      // Build media array with type and timing info
      const mediaItems = sections.map((section, index) => {
        // Enforce minimum duration of 5 seconds for all clips
        const MIN_DURATION = 5;
        const duration = Math.max(section.duration || MIN_DURATION, MIN_DURATION);
        
        const baseItem = {
          duration: duration,
          section_name: section.name,
          order: index,
          media_type: section.mediaType
        };
        
        if (section.mediaType === 'video' && section.assignedVideo) {
          return {
            ...baseItem,
            public_id: section.assignedVideo.public_id
          };
        } else if (section.mediaType === 'image' && section.assignedImage) {
          return {
            ...baseItem,
            public_id: section.assignedImage.public_id
          };
        }
        return baseItem;
      });

      const requestData = {
        media_items: mediaItems,
        selected_formats: selectedFormats,
        transition: transition,
        logo_enabled: logoEnabled && companyLogo !== null,
        logo_public_id: logoEnabled ? companyLogo?.public_id : null,
        logo_position: logoPosition,
        logo_scale: logoScale,
        logo_padding: logoPadding,
        logo_opacity: logoOpacity
      };

      const response = await axios.post(`${API}/presentour/process`, requestData, {
        timeout: 300000
      });

      if (response.data && response.data.success) {
        setGeneratedVideos(response.data);
        toast.success(`Presentour video generated!`);
        
        setTimeout(() => {
          document.getElementById("presentour-results")?.scrollIntoView({ behavior: "smooth" });
        }, 100);
      } else {
        toast.error("Failed to generate Presentour video");
      }
    } catch (error) {
      console.error("Processing error:", error);
      toast.error(error.response?.data?.detail || "Failed to generate video");
    } finally {
      clearInterval(messageInterval);
      setIsProcessing(false);
      setProcessingMessage("");
      setProcessingStep(0);
    }
  };

  // Copy URL
  const copyUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("URL copied");
    } catch (error) {
      toast.error("Failed to copy URL");
    }
  };

  // If processing, show loading screen
  if (isProcessing) {
    return (
      <div className="video-builder">
        <div className="video-header">
          <h1 className="video-title">Presentour Video Builder</h1>
        </div>
        <div className="video-content processing-container">
          <div className="processing-screen">
            <div className="processing-animation">
              <Loader2 className="processing-spinner" />
              <div className="processing-ring"></div>
            </div>
            <h2 className="processing-title">Creating Your Presentour Video</h2>
            <p className="processing-message">{processingMessage}</p>
            <div className="processing-steps">
              <div className={`step-dot ${processingStep >= 0 ? 'active' : ''}`}></div>
              <div className={`step-dot ${processingStep >= 1 ? 'active' : ''}`}></div>
              <div className={`step-dot ${processingStep >= 2 ? 'active' : ''}`}></div>
              <div className={`step-dot ${processingStep >= 3 ? 'active' : ''}`}></div>
              <div className={`step-dot ${processingStep >= 4 ? 'active' : ''}`}></div>
            </div>
            <p className="processing-note">Syncing video to your script timing...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="video-builder">
      {/* Header */}
      <AppHeader onBack={onBack} />

      <div className="video-content">
        {/* Show results if we have them */}
        {generatedVideos ? (
          <div id="presentour-results" className="results-section">
            <div className="results-header">
              <h2 className="results-title">Presentour Video Ready</h2>
              <div className="results-actions">
                <span className="results-count">{generatedVideos.total_count} video(s)</span>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setGeneratedVideos(null)}
                >
                  Build Another
                </Button>
              </div>
            </div>

            <div className="results-group featured">
              <h3 className="group-title">
                <Video className="group-icon" />
                Script-Synced Video
              </h3>
              <div className="videos-grid stitched">
                {generatedVideos.outputs?.map((video) => (
                  <div key={video.id} className="video-card stitched">
                    <div className="video-preview">
                      <video 
                        src={video.secure_url} 
                        controls 
                        preload="metadata"
                      />
                    </div>
                    <div className="video-info">
                      <span className="video-label">{video.format_label}</span>
                      <span className="video-duration">{formatDuration(getTotalDuration())}</span>
                    </div>
                    <div className="video-actions">
                      <a 
                        href={video.secure_url}
                        className="video-btn"
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Open in new tab"
                        data-testid={`open-video-${video.id}`}
                      >
                        <ExternalLink className="video-btn-icon" />
                      </a>
                      <button 
                        className="video-btn"
                        onClick={() => copyUrl(video.secure_url)}
                        title="Copy Preview URL"
                        data-testid={`copy-url-${video.id}`}
                      >
                        <Copy className="video-btn-icon" />
                      </button>
                    </div>
                    {/* Dedicated Download Button */}
                    <a 
                      href={video.download_url}
                      download={video.filename}
                      className="download-button-large"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`download-video-${video.id}`}
                    >
                      <Download className="download-icon" />
                      Download Video
                    </a>
                    {/* Copy Download Link Button */}
                    <button 
                      className="copy-download-link-btn"
                      onClick={() => {
                        copyUrl(video.download_url);
                        toast.success("Download link copied!");
                      }}
                      data-testid={`copy-download-link-${video.id}`}
                    >
                      <Copy size={14} />
                      Copy Download Link
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Script breakdown */}
            <div className="script-breakdown">
              <h4>Script Timing Breakdown</h4>
              <div className="breakdown-list">
                {sections.map((section, index) => (
                  <div key={index} className="breakdown-item">
                    <span className="breakdown-name">{section.name}</span>
                    <span className="breakdown-duration">{formatDuration(section.duration)}</span>
                  </div>
                ))}
                <div className="breakdown-total">
                  <span>Total Duration</span>
                  <span>{formatDuration(getTotalDuration())}</span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Show form if no results yet */
          <div className="video-form">
            {/* Script Sections Overview */}
            <section className="form-section">
              <Label className="section-title">
                <FileText className="section-icon" />
                Script Sections ({sections.length})
              </Label>
              
              {sections.length === 0 ? (
                <div className="empty-sections">
                  <p>No script sections loaded. Please generate a script first from the Script Builder.</p>
                  <Button onClick={onBack} variant="outline">
                    <ArrowLeft className="btn-icon" />
                    Go to Script Builder
                  </Button>
                </div>
              ) : (
                <div className="sections-list">
                  {sections.map((section, index) => {
                    const hasMedia = sectionHasMedia(section);
                    return (
                      <div 
                        key={index} 
                        className={`section-card ${hasMedia ? 'has-media' : ''}`}
                      >
                        <div className="section-main">
                          <div className="section-info">
                            <div className="section-number">{index + 1}</div>
                            <div className="section-details">
                              <span className="section-name">{section.name}</span>
                              <span className="section-duration">
                                <Clock size={12} />
                                {section.duration < 5 ? (
                                  <span className="duration-extended">
                                    {formatDuration(section.duration)} → 0:05
                                  </span>
                                ) : (
                                  formatDuration(section.duration)
                                )}
                              </span>
                            </div>
                          </div>
                          
                          {/* Media Type Toggle */}
                          <div className="media-type-toggle">
                            <button
                              className={`type-btn ${section.mediaType === 'video' ? 'active' : ''}`}
                              onClick={() => toggleSectionMediaType(index)}
                            >
                              <Video size={14} />
                              Video
                            </button>
                            <button
                              className={`type-btn ${section.mediaType === 'image' ? 'active' : ''}`}
                              onClick={() => toggleSectionMediaType(index)}
                            >
                              <Image size={14} />
                              Image
                            </button>
                          </div>
                        </div>
                        
                        {/* Media Assignment Area */}
                        <div className="section-media-area">
                          {section.mediaType === 'video' ? (
                            /* Video Selection */
                            <div className="section-video-select">
                              <Select 
                                value={section.assignedVideo?.id || "none"}
                                onValueChange={(val) => {
                                  if (val === "none") {
                                    assignVideoToSection(index, null);
                                  } else {
                                    assignVideoToSection(index, val);
                                  }
                                }}
                              >
                                <SelectTrigger className="video-select-trigger">
                                  <SelectValue placeholder="Select video..." />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">-- Select video --</SelectItem>
                                  {uploadedClips.map(clip => (
                                    <SelectItem 
                                      key={clip.id} 
                                      value={clip.id}
                                    >
                                      {clip.original_filename}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              {section.assignedVideo && (
                                <div className="assigned-thumb">
                                  <img 
                                    src={section.assignedVideo.thumbnail_url} 
                                    alt={section.assignedVideo.original_filename}
                                  />
                                  <CheckCircle className="assigned-check" size={14} />
                                </div>
                              )}
                            </div>
                          ) : (
                            /* Image Upload/Preview */
                            <div className="section-image-area">
                              {section.assignedImage ? (
                                <div className="section-image-preview">
                                  <img src={section.assignedImage.secure_url} alt="Section" />
                                  <button 
                                    className="remove-section-image"
                                    onClick={() => removeSectionImage(index)}
                                  >
                                    <X size={12} />
                                  </button>
                                  <CheckCircle className="assigned-check" size={14} />
                                </div>
                              ) : (
                                <div 
                                  className="section-image-upload"
                                  onClick={() => sectionImageRefs.current[index]?.click()}
                                >
                                  {uploadingSectionImage === index ? (
                                    <Loader2 className="animate-spin" size={18} />
                                  ) : (
                                    <>
                                      <Upload size={16} />
                                      <span>Upload</span>
                                    </>
                                  )}
                                </div>
                              )}
                              <input
                                ref={el => sectionImageRefs.current[index] = el}
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleSectionImageUpload(e, index)}
                                style={{ display: "none" }}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  
                  <div className="total-duration-bar">
                    <span>Total Script Duration:</span>
                    <span className="total-time">{formatDuration(getTotalDuration())}</span>
                  </div>
                  
                  {/* Tip about video requirement */}
                  <p className="sections-tip">
                    💡 Tip: Make the first section a <strong>video clip</strong> for best results. If your first section is an image, it will appear at the end of the video.
                  </p>
                </div>
              )}
            </section>

            {/* Upload Section */}
            <section className="form-section">
              <Label className="section-title">
                <Upload className="section-icon" />
                Upload Video Clips
              </Label>
              
              {/* Smart File Management Option */}
              <div className="smart-file-option">
                <div className="smart-file-row">
                  <div className="smart-file-label">
                    <Wand2 className="smart-icon" />
                    <span>Smart File Management</span>
                    <span className="coming-soon-badge">Coming Soon</span>
                  </div>
                  <Checkbox 
                    checked={smartFileManagement}
                    onCheckedChange={setSmartFileManagement}
                    disabled={true}
                  />
                </div>
                <p className="smart-file-hint">
                  Auto-match videos to sections based on filename (e.g., &quot;kitchen.mp4&quot; → Kitchen)
                </p>
              </div>
              
              <div 
                className={`upload-zone ${isUploading ? 'uploading' : ''}`}
                onClick={() => !isUploading && fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="video/*"
                  onChange={handleFileSelect}
                  style={{ display: "none" }}
                />
                {isUploading ? (
                  <div className="upload-loading">
                    <div className="upload-progress-container">
                      <div className="upload-progress-ring">
                        <svg viewBox="0 0 100 100">
                          <circle className="progress-bg" cx="50" cy="50" r="45" />
                          <circle 
                            className="progress-bar" 
                            cx="50" 
                            cy="50" 
                            r="45"
                            style={{ strokeDashoffset: 283 - (283 * uploadProgress) / 100 }}
                          />
                        </svg>
                        <span className="progress-text">
                          {uploadProgress >= 99 ? <Loader2 className="mini-spinner" size={18} /> : `${uploadProgress}%`}
                        </span>
                      </div>
                    </div>
                    <span className="upload-status">
                      {uploadProgress >= 99 ? 'Processing...' : 'Uploading videos...'}
                    </span>
                  </div>
                ) : (
                  <div className="upload-prompt">
                    <Video className="upload-icon" />
                    <span className="upload-text">Click or drag videos here</span>
                    <span className="upload-hint">Upload one video per script section</span>
                  </div>
                )}
              </div>

              {/* Uploaded Clips */}
              {uploadedClips.length > 0 && (
                <div className="clips-section">
                  <div className="clips-header">
                    <span className="clips-count">{uploadedClips.length} clip{uploadedClips.length !== 1 ? 's' : ''} uploaded</span>
                  </div>
                  <div className="clips-grid">
                    {uploadedClips.map((clip) => (
                      <div key={clip.id} className="upload-clip-card">
                        <div className="upload-clip-thumb">
                          <img src={clip.thumbnail_url} alt={clip.original_filename} />
                          <span className="upload-clip-duration">{formatDuration(clip.duration)}</span>
                        </div>
                        <div className="upload-clip-info">
                          <span className="upload-clip-name">{clip.original_filename}</span>
                          {clip.assignedSection !== null && (
                            <span className="assigned-badge">
                              <CheckCircle size={10} />
                              {sections[clip.assignedSection]?.name}
                            </span>
                          )}
                        </div>
                        <button
                          className="remove-upload-clip"
                          onClick={() => removeClip(clip.id, clip.public_id)}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </section>

            {/* Output Formats */}
            <section className="form-section">
              <Label className="section-title">
                <Video className="section-icon" />
                Output Formats
              </Label>
              <div className="formats-grid">
                {VIDEO_PRESETS.map((preset) => (
                  <button
                    key={preset.key}
                    type="button"
                    className={`format-button ${selectedFormats.includes(preset.key) ? 'selected' : ''}`}
                    onClick={() => toggleFormat(preset.key)}
                  >
                    <CheckCircle className={`check-icon ${selectedFormats.includes(preset.key) ? 'visible' : ''}`} />
                    <span className="format-label">{preset.label}</span>
                    <span className="format-dimensions">{preset.dimensions}</span>
                    {preset.primary && <span className="format-badge">Primary</span>}
                  </button>
                ))}
              </div>
            </section>

            {/* Transition Option */}
            <section className="form-section">
              <Label className="section-title">
                <Play className="section-icon" />
                Transition Between Clips
              </Label>
              <div className="transition-options">
                <button
                  type="button"
                  className={`transition-btn ${transition === 'none' ? 'selected' : ''}`}
                  onClick={() => setTransition('none')}
                >
                  <span className="transition-name">Cut</span>
                  <span className="transition-desc">Instant switch</span>
                </button>
                <button
                  type="button"
                  className={`transition-btn ${transition === 'crossfade' ? 'selected' : ''}`}
                  onClick={() => setTransition('crossfade')}
                >
                  <span className="transition-name">Crossfade</span>
                  <span className="transition-desc">1 sec dissolve</span>
                </button>
              </div>
            </section>

            {/* Branding Section */}
            <div className="toggle-section">
              <div className="toggle-row">
                <div className="toggle-label">
                  <Image className="toggle-icon" />
                  <span>Add Logo Watermark</span>
                </div>
                <Switch
                  checked={logoEnabled}
                  onCheckedChange={setLogoEnabled}
                />
              </div>

              {logoEnabled && (
                <div className="branding-options">
                  {/* Logo Upload */}
                  <div className="logo-upload-section">
                    {companyLogo ? (
                      <div className="logo-preview">
                        <img src={companyLogo.secure_url} alt="Logo" className="logo-image" />
                        <div className="logo-actions">
                          <button 
                            className="logo-change-btn"
                            onClick={() => logoInputRef.current?.click()}
                          >
                            Change
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div 
                        className="logo-dropzone"
                        onClick={() => logoInputRef.current?.click()}
                      >
                        {isUploadingLogo ? (
                          <Loader2 className="animate-spin" size={20} />
                        ) : (
                          <>
                            <Upload size={20} />
                            <span>Upload Logo</span>
                          </>
                        )}
                      </div>
                    )}
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      style={{ display: "none" }}
                    />
                  </div>

                  {/* Logo Settings */}
                  {companyLogo && (
                    <div className="logo-settings">
                      <div className="setting-row">
                        <Label className="setting-label">Position</Label>
                        <Select value={logoPosition} onValueChange={setLogoPosition}>
                          <SelectTrigger className="select-trigger-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {LOGO_POSITIONS.map(pos => (
                              <SelectItem key={pos.value} value={pos.value}>{pos.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="setting-row">
                        <Label className="setting-label">
                          Size
                          <span className="setting-value">{logoScale}%</span>
                        </Label>
                        <Slider
                          value={[logoScale]}
                          onValueChange={(val) => setLogoScale(val[0])}
                          min={5}
                          max={90}
                          step={1}
                          className="setting-slider"
                        />
                      </div>

                      <div className="setting-row">
                        <Label className="setting-label">
                          Padding
                          <span className="setting-value">{logoPadding}px</span>
                        </Label>
                        <Slider
                          value={[logoPadding]}
                          onValueChange={(val) => setLogoPadding(val[0])}
                          min={0}
                          max={60}
                          step={2}
                          className="setting-slider"
                        />
                      </div>

                      <div className="setting-row">
                        <Label className="setting-label">
                          Opacity
                          <span className="setting-value">{logoOpacity}%</span>
                        </Label>
                        <Slider
                          value={[logoOpacity]}
                          onValueChange={(val) => setLogoOpacity(val[0])}
                          min={20}
                          max={100}
                          step={5}
                          className="setting-slider"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Generate Button */}
            <Button
              onClick={handleGenerate}
              disabled={isProcessing || sections.length === 0 || !allSectionsHaveMedia()}
              className="generate-button"
            >
              <Play className="btn-icon" />
              Generate Presentour Video
            </Button>
            
            {sections.length > 0 && !allSectionsHaveMedia() && (
              <p className="generate-hint">
                Assign media (video or image) to each script section to continue
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default PresentourBuilder;
